import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

// Focus management for shell layers (contract §2 Accessibility). Layers that
// go under another one become `inert`, which drops whatever was focused inside
// them; these helpers move focus into the layer the user now sees and bring it
// back to the remembered element when that layer is uncovered again.

const TEXT_INPUT_TYPES = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'password', 'number']);

/** Text fields are never refocused programmatically: on phones that would pop up the keyboard. */
function isTextEntry(element: Element): boolean {
  if (element instanceof HTMLTextAreaElement) return true;
  if (element instanceof HTMLInputElement) return TEXT_INPUT_TYPES.has(element.type);
  return element instanceof HTMLElement && element.isContentEditable;
}

function tryFocus(element: HTMLElement): boolean {
  element.focus({ preventScroll: true });
  return document.activeElement === element;
}

/**
 * True when focus has nowhere useful to stay: nothing is focused, or the
 * focused element was removed or sits in an inert (covered or leaving) layer.
 * Focus already inside `layer`, or on live UI elsewhere (the nav bar, a
 * sheet), is left alone.
 */
export function focusIsStranded(layer: HTMLElement): boolean {
  const active = document.activeElement;
  if (!active || active === document.body || active === document.documentElement) return true;
  if (layer.contains(active)) return false;
  return !active.isConnected || active.closest('[inert]') !== null;
}

/** Focuses `remembered` when it is still a usable target inside `container`, else the container itself. */
export function restoreFocus(container: HTMLElement, remembered: Element | null): void {
  if (
    remembered instanceof HTMLElement &&
    remembered !== container &&
    remembered.isConnected &&
    container.contains(remembered) &&
    remembered.closest('[inert]') === null &&
    !isTextEntry(remembered) &&
    tryFocus(remembered)
  ) {
    return;
  }
  tryFocus(container);
}

/** Moves focus to a newly shown layer: its page title (`h1`) when rendered, else the layer container. */
export function focusLayerStart(layer: HTMLElement): void {
  const heading = layer.querySelector('h1');
  if (heading instanceof HTMLElement && heading.closest('[inert]') === null) {
    if (!heading.hasAttribute('tabindex')) heading.tabIndex = -1;
    if (tryFocus(heading)) return;
  }
  tryFocus(layer);
}

/** The element most recently focused inside `ref` (kept while the layer is covered). */
export function useFocusMemory(ref: RefObject<HTMLElement | null>): RefObject<Element | null> {
  const last = useRef<Element | null>(null);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const onFocusIn = (event: FocusEvent) => {
      if (event.target instanceof Element) last.current = event.target;
    };
    element.addEventListener('focusin', onFocusIn);
    return () => element.removeEventListener('focusin', onFocusIn);
  }, [ref]);
  return last;
}

/** When the layer is uncovered (`active` turns true again), focus returns to where it was. */
export function useRestoreFocusOnReturn(ref: RefObject<HTMLElement | null>, memory: RefObject<Element | null>, active: boolean): void {
  const wasActive = useRef(active);
  useLayoutEffect(() => {
    const returned = active && !wasActive.current;
    wasActive.current = active;
    const element = ref.current;
    if (returned && element && focusIsStranded(element)) restoreFocus(element, memory.current);
  }, [active, ref, memory]);
}

/**
 * Focuses a layer that is opening, unless a feature inside it already took
 * focus or focus sits on live UI elsewhere (a sheet, a toast). When only the
 * bare container has focus, the page title takes it over.
 */
export function takeLayerFocus(layer: HTMLElement): void {
  if (focusIsStranded(layer) || document.activeElement === layer) focusLayerStart(layer);
}

/**
 * Moves focus into the layer `ref` points at when the calling component
 * mounts (`enabled`: opened by the user, not rebuilt on a cold start). Call it
 * from the component that renders the layer element: React attaches a
 * parent's ref only after its children's layout effects ran, but before the
 * owner's own, so the layer element is always there.
 */
export function useTakeFocusOnOpen(ref: RefObject<HTMLElement | null>, enabled: boolean): void {
  useLayoutEffect(() => {
    const layer = ref.current;
    if (enabled && layer) takeLayerFocus(layer);
    // Mount only: a layer takes focus when it opens, not on later renders.
  }, []);
}

/**
 * Moves focus into the layer once lazily loaded content commits. Render it
 * inside the layer's Suspense boundary, next to the screen: when the screen
 * suspends, the layer opened with a bare container (focused by
 * `useTakeFocusOnOpen`) and this lets the page title take over when it
 * arrives. When it mounts together with the layer element, the parent's ref
 * is not attached yet during this layout effect, so the check runs once the
 * commit has finished (a microtask, still before the next paint).
 */
export function TakeFocus({ layerRef, enabled }: { layerRef: RefObject<HTMLElement | null>; enabled: boolean }): null {
  useLayoutEffect(() => {
    if (!enabled) return;
    const layer = layerRef.current;
    if (layer) {
      takeLayerFocus(layer);
      return;
    }
    let cancelled = false;
    queueMicrotask(() => {
      const attached = layerRef.current;
      if (!cancelled && attached) takeLayerFocus(attached);
    });
    return () => {
      cancelled = true;
    };
    // Mount only: a layer takes focus when it opens, not on later renders.
  }, []);
  return null;
}

/** Set while the main navigation unmounts with focus inside it, until its replacement mounts. */
let navFocusHandOff = 0;
const NAV_HAND_OFF_MS = 1000;

/**
 * The window crossed the desktop breakpoint while focus was in the main
 * navigation: the sidebar and the bottom nav bar replace each other, and the
 * one that mounts takes focus on its current link (else its first link)
 * instead of leaving it on `<body>`. Call it in both, with a ref to the `nav`.
 */
export function useNavigationFocusHandOff(ref: RefObject<HTMLElement | null>): void {
  useLayoutEffect(() => {
    const nav = ref.current;
    const active = document.activeElement;
    const lost = !active || active === document.body || active === document.documentElement;
    if (nav && lost && navFocusHandOff > 0 && performance.now() - navFocusHandOff < NAV_HAND_OFF_MS) {
      const target = nav.querySelector<HTMLElement>('[aria-current="page"]') ?? nav.querySelector<HTMLElement>('a[href], button');
      target?.focus({ preventScroll: true });
    }
    navFocusHandOff = 0;
    return () => {
      // Runs before React removes the element, so focus is still where the user left it.
      if (nav?.contains(document.activeElement)) navFocusHandOff = performance.now();
    };
  }, [ref]);
}
